import { useContext, useEffect } from 'react';

import { ScriptProviderProps } from '@paypal/react-paypal-js';

import { useAppSelector } from '@/store/hooks';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { ProvidersLibsDispatchContext } from '@/services/providersLibs/providersLibsContext';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

const buildPaypalOptions = (
  paypalClientId: string,
  paymentSelectState: PaymentSelectState,
): ScriptProviderProps['options'] => ({
  clientId: paypalClientId,
  currency: paymentSelectState.currency,
  components: 'buttons', // Using defaults from react-donation
  'disable-funding': 'card,credit', // Using defaults from react-donation
});

function PaypalLoader() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const { getProviderPaypalClientId } = usePaymentsConfig();
  const paypalClientId = getProviderPaypalClientId();

  const { isProviderAvailable } = usePaymentsConfig();

  const providersLibsDispatch = useContext(ProvidersLibsDispatchContext);

  // Set paypalOptions for use by PaypalContainer & PaypalButton
  useEffect(() => {
    // Only load Paypal if it is enabled in payments config
    if (!isProviderAvailable('single', 'PAYPAL')) {
      return;
    }

    // Wait for Paypal provider config
    if (!paypalClientId) {
      return;
    }

    // Generate Paypal options
    // API Reference: https://developer.paypal.com/sdk/js/reference/#link-buttons
    const paypalOptions = buildPaypalOptions(paypalClientId, paymentSelectState);

    providersLibsDispatch({
      paypalOptions,
    });
  }, [
    isProviderAvailable,
    paypalClientId,
    paymentSelectState,
    providersLibsDispatch,
  ]);

  return null;
}

export default PaypalLoader;
